import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "dialog-feedback" }

import { computed, PropType, ref, ComputedRef } from 'vue';
import FormDialog from './DialogForm.vue';
import useTranslations from '@/composables/useTranslations';
import FormInput from '@/components/common/form/formInput/FormInput.vue';
import FormTextArea from '@/components/common/form/formTextArea/FormTextArea.vue';
import { layoutAreaTypes } from '@/types/layoutTypes';
import accountApiService from '@/services/account.api.service';
import { ApiError, ApiSuccess } from '@/types/commonTypes';
import helpers from '@/helpers';
import { AxiosResponse } from 'axios';
import { useUserStore } from '@/stores/user.store';

type FormData = {
  name: string | null;
  feedbackAuthorEmail: string | null;
  title: string;
  message: string;
};

type FormErrors = {
  name: string | null;
  feedbackAuthorEmail: string | null;
  title: string | null;
  message: string | null;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'DialogFeedback',
  props: {
  layoutAreaType: {
    type: String as PropType<layoutAreaTypes>,
    required: true,
  },
},
  setup(__props, { expose: __expose }) {

const props = __props;

__expose({ openDialog });

const userStore = useUserStore();
const { translate: t } = useTranslations();

const formDialogRef = ref();
const isGuestFeedback = ref<boolean>();
const isDialogOpen = ref<boolean>(false);
const formData = ref<FormData>(initFormData());
const formErrors = ref<FormErrors>(initFormErrors());
const errorApiResponse = ref<string[] | null>(null);
const successApiResponse = ref<string | null>(null);
const isLoading = ref<boolean>(false);

const isFormValid = computed(() => Object.values(formErrors.value).every((value) => !value));

const labels: ComputedRef<{ [key: string]: string }> = computed(() => {
  return {
    name: t('Name'),
    feedbackAuthorEmail: t('Register.Email'),
    title: t('MailingData.TitleTemplate'),
    message: t('Message'),
  };
});

function initFormData() {
  return {
    name: isGuestFeedback.value ? '' : null,
    feedbackAuthorEmail: isGuestFeedback.value ? '' : null,
    title: '',
    message: '',
  };
}

function initFormErrors() {
  return {
    name: null,
    feedbackAuthorEmail: null,
    title: null,
    message: null,
  };
}

async function handleSendBtnClick() {
  if (!validateForm()) return;

  isLoading.value = true;

  if (isGuestFeedback.value) {
    await sendGuestUserFeedback()
      .then((response) => handleApiResponse(response))
      .finally(() => (isLoading.value = false));
  } else {
    await sendUserFeedback()
      .then((response) => handleApiResponse(response))
      .finally(() => (isLoading.value = false));
  }
}

async function sendGuestUserFeedback() {
  return await accountApiService.sendGuestUserFeedback({
    name: formData.value.name as string,
    feedbackAuthorEmail: formData.value.feedbackAuthorEmail as string,
    title: formData.value.title,
    message: formData.value.message,
  });
}

async function sendUserFeedback() {
  return await accountApiService.sendUserFeedback({
    title: formData.value.title,
    message: formData.value.message,
  });
}

function handleApiResponse(response: AxiosResponse<ApiSuccess | ApiError>) {
  if (helpers.axios.isStatusOk(response)) {
    if (helpers.axios.isSuccess(response) && response.data?.successMessage) {
      successApiResponse.value = response.data.successMessage;
    }
    formDialogRef.value.hideFormContent();
    formDialogRef.value.hideConfirmBtn();
  } else if (helpers.axios.isStatusError(response)) {
    errorApiResponse.value = helpers.axios.getErrorMessages(response);
  }
}

function handleCancelBtnClick() {
  closeDialog();
}

function validateForm() {
  (Object.keys(formData.value) as Array<keyof FormData>)
    .filter((key) => {
      return formData.value[key] !== null;
    })
    .forEach((field) => {
      validateField(field);
    });

  if (formData.value.feedbackAuthorEmail) {
    validateEmail();
  }

  return (Object.keys(formErrors.value) as Array<keyof FormErrors>).every(
    (key) => !formErrors.value[key],
  );
}

function validateField(field: keyof FormData) {
  if (!formData.value[field]) {
    formErrors.value[field] = t('Error.FieldRequired', [labels.value[field]]);
  } else {
    formErrors.value[field] = null;
  }
}

function validateEmail() {
  if (!helpers.email.isValidEmail(formData.value.feedbackAuthorEmail as string)) {
    formErrors.value.feedbackAuthorEmail = t('Usr.InvalidPasswordEmail');
  }
}

function openDialog() {
  isGuestFeedback.value =
    props.layoutAreaType === layoutAreaTypes.Unlogged || userStore?.userIsGuest;
  formData.value = initFormData();
  formErrors.value = initFormErrors();
  successApiResponse.value = null;
  errorApiResponse.value = null;
  isDialogOpen.value = true;
}

function closeDialog() {
  isDialogOpen.value = false;
}

return (_ctx: any,_cache: any) => {
  return (isDialogOpen.value)
    ? (_openBlock(), _createBlock(FormDialog, {
        key: 0,
        ref_key: "formDialogRef",
        ref: formDialogRef,
        title: _unref(t)('Feedback.FormTitle'),
        "confirm-button-disabled": isLoading.value || !isFormValid.value,
        "success-notification": successApiResponse.value,
        "error-notification": errorApiResponse.value,
        "is-loading": isLoading.value,
        onConfirmBtnClicked: handleSendBtnClick,
        onCancelBtnClicked: handleCancelBtnClick,
        onClose: closeDialog
      }, {
        default: _withCtx(() => [
          _createElementVNode("form", _hoisted_1, [
            (isGuestFeedback.value)
              ? (_openBlock(), _createBlock(FormInput, {
                  key: 0,
                  modelValue: formData.value.name,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.value.name) = $event)),
                  type: "text",
                  name: "name",
                  readonly: isLoading.value,
                  label: labels.value['name'],
                  size: 'large',
                  width: '100%',
                  autocomplete: 'off',
                  "is-required": true,
                  "is-valid": formErrors.value?.name === null,
                  "validation-message": formErrors.value?.name,
                  onInput: _cache[1] || (_cache[1] = () => validateField('name'))
                }, null, 8, ["modelValue", "readonly", "label", "is-valid", "validation-message"]))
              : _createCommentVNode("", true),
            (isGuestFeedback.value)
              ? (_openBlock(), _createBlock(FormInput, {
                  key: 1,
                  modelValue: formData.value.feedbackAuthorEmail,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.value.feedbackAuthorEmail) = $event)),
                  type: "text",
                  name: "feedbackAuthorEmail",
                  readonly: isLoading.value,
                  label: labels.value['feedbackAuthorEmail'],
                  size: 'large',
                  width: '100%',
                  autocomplete: 'off',
                  "is-required": true,
                  "is-valid": formErrors.value?.feedbackAuthorEmail === null,
                  "validation-message": formErrors.value?.feedbackAuthorEmail,
                  onInput: _cache[3] || (_cache[3] = () => validateField('feedbackAuthorEmail'))
                }, null, 8, ["modelValue", "readonly", "label", "is-valid", "validation-message"]))
              : _createCommentVNode("", true),
            _createVNode(FormInput, {
              modelValue: formData.value.title,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formData.value.title) = $event)),
              type: "text",
              name: "title",
              readonly: isLoading.value,
              label: labels.value['title'],
              size: 'large',
              width: '100%',
              autocomplete: 'off',
              "is-required": true,
              "is-valid": formErrors.value?.title === null,
              "validation-message": formErrors.value?.title,
              onInput: _cache[5] || (_cache[5] = () => validateField('title'))
            }, null, 8, ["modelValue", "readonly", "label", "is-valid", "validation-message"]),
            _createVNode(FormTextArea, {
              modelValue: formData.value.message,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((formData.value.message) = $event)),
              type: "text",
              name: "message",
              readonly: isLoading.value,
              label: labels.value['message'],
              width: '100%',
              "is-required": true,
              "auto-size": true,
              rows: 10,
              "is-valid": formErrors.value?.message === null,
              "validation-message": formErrors.value?.message,
              onInput: _cache[7] || (_cache[7] = () => validateField('message'))
            }, null, 8, ["modelValue", "readonly", "label", "is-valid", "validation-message"])
          ])
        ]),
        _: 1
      }, 8, ["title", "confirm-button-disabled", "success-notification", "error-notification", "is-loading"]))
    : _createCommentVNode("", true)
}
}

})